/**
 * Chart dataset construction composable.
 *
 * Aggregates raw data by view level (year/month/day), builds forecast lines,
 * constructs Chart.js datasets including neighbor overlays, and provides
 * chart options.
 */

import { ref, computed, watchEffect } from 'vue'
import { format, getDaysInMonth, parseISO } from 'date-fns'
import { NEIGHBOR_COLORS, METER_COLORS, MONTHLY_SOLAR_FRACTIONS } from '../constants'
import { getSunriseSunset, findNearestLabelIndex } from '../solar'

export function useChartDatasets({
  props,
  level,
  viewYear,
  viewMonth,
  viewDay,
  rawData,
  monthNames,
  emit,
  // Neighbor data
  showNeighbors,
  showActivePower,
  showNormalized,
  // Previous period comparison
  showPrevious,
  previousRawData,
  previousRange,
  // Day view: false trims the night, see dayWindow
  showFullDay,
  activeNeighbors,
  neighborDataMap,
  neighborWpMap,
  mainTotalWp,
  normalizeValues,
  // The address's meters separately instead of their sum: [{ serial, label }] and serial -> rows
  splitMeters,
  splitDataMap,
}) {

  // ─── Data aggregation ──────────────────────────────────────────────────────
  // Day view without the night: from half an hour before sunrise until half an hour after sunset,
  // in minutes from midnight. Null shows the whole day (also when there is no sunrise, polar).
  const DAY_MARGIN_MINUTES = 30
  const dayWindow = computed(() => {
    if (showFullDay.value || level.value !== 'day') return null
    const times = getSunriseSunset(new Date(viewYear.value, viewMonth.value, viewDay.value))
    if (!times) return null
    return { from: times.sunrise - DAY_MARGIN_MINUTES, until: times.sunset + DAY_MARGIN_MINUTES }
  })

  // One period's rows to labels and values; year and month are the period's own (days in that month)
  const aggregate = (rows, periodLevel, year, month) => {
    if (!rows) return { labels: [], values: [] }

    if (periodLevel === 'day') {
      const minutes = item => {
        const date = parseISO(item.date)
        return date.getHours() * 60 + date.getMinutes()
      }
      const daylight = dayWindow.value
      const dayRows = daylight ? rows.filter(item => minutes(item) >= daylight.from && minutes(item) <= daylight.until) : rows
      return {
        labels: dayRows.map(item => format(parseISO(item.date), 'HH:mm')),
        values: dayRows.map(item => item.delta_power ?? 0),
        activePower: dayRows.map(item => item.active_power != null ? item.active_power / 4 : null),
      }
    }

    if (periodLevel === 'month') {
      const daysInMonth = getDaysInMonth(new Date(year, month, 1))
      const byDay = {}
      for (let d = 1; d <= daysInMonth; d++) byDay[d] = 0
      rows.forEach(item => {
        const day = parseISO(item.date).getDate()
        byDay[day] = (byDay[day] || 0) + Math.max(0, item.production ?? 0)
      })
      return {
        labels: Object.keys(byDay).map(String),
        values: Object.values(byDay),
        days: Object.keys(byDay).map(Number),
      }
    }

    // Year view
    const byMonth = {}
    for (let m = 0; m < 12; m++) byMonth[m] = 0
    rows.forEach(item => {
      const month = parseISO(item.date).getMonth()
      byMonth[month] = (byMonth[month] || 0) + Math.max(0, item.production ?? 0)
    })
    return {
      labels: monthNames,
      values: Object.values(byMonth),
      months: Object.keys(byMonth).map(Number),
    }
  }

  const aggregatedData = computed(() =>
    aggregate(rawData.value, level.value, viewYear.value, viewMonth.value)
  )

  // Rows of another series (a neighbor, or one meter of the address) on the main series' labels
  const alignToLabels = (rows) => {
    if (level.value === 'day') {
      const byTime = {}
      rows.forEach(item => {
        byTime[format(parseISO(item.date), 'HH:mm')] = Math.max(0, item.delta_power ?? 0)
      })
      return aggregatedData.value.labels.map(label => byTime[label] ?? null)
    }
    return aggregate(rows, level.value, viewYear.value, viewMonth.value).values
  }

  // Per meter: the sum, its forecast and the previous period are left out, they belong to the whole address
  const isSplit = computed(() => (splitMeters.value?.length ?? 0) > 0)

  // The previous period on the current period's axis: by time of day, day of month or month.
  // Days the previous month did not have (31 after a 30-day month) stay empty.
  const previousData = computed(() => {
    if (!showPrevious.value || !previousRawData.value) return null
    const from = previousRange.value.from
    const previous = aggregate(previousRawData.value, level.value, from.getFullYear(), from.getMonth())
    const byLabel = Object.fromEntries(previous.labels.map((label, i) => [label, previous.values[i]]))
    return aggregatedData.value.labels.map(label => byLabel[label] ?? null)
  })

  const previousDatasetLabel = computed(() => {
    const from = previousRange.value?.from
    if (!from) return ''
    if (level.value === 'year') return `Vorig jaar (${from.getFullYear()})`
    if (level.value === 'month') return `Vorige maand (${monthNames[from.getMonth()].toLowerCase()} ${from.getFullYear()})`
    return `Vorige dag (${from.getDate()} ${monthNames[from.getMonth()].toLowerCase()})`
  })

  // Emit max day power for panel calculation
  watchEffect(() => {
    if (level.value !== 'day') return
    const values = aggregatedData.value.values
    if (!values || values.length === 0) return
    const max = Math.max(...values)
    if (max > 0)
      emit('max-day-power', {
        power: max,
        date: new Date(viewYear.value, viewMonth.value, viewDay.value),
      })
  })

  // ─── Forecast lines ────────────────────────────────────────────────────────
  const prognoseData = computed(() => null) // Day-level removed

  const monthPrognoseData = computed(() => {
    if (level.value !== 'month' || !props.expectedProduction) return null
    const monthFraction = MONTHLY_SOLAR_FRACTIONS[viewMonth.value] ?? (1 / 12)
    const monthlyWh = props.expectedProduction * monthFraction
    const daysInMonth = getDaysInMonth(new Date(viewYear.value, viewMonth.value, 1))
    const dailyWh = monthlyWh / daysInMonth
    const values = []
    for (let d = 1; d <= daysInMonth; d++) values.push(Math.round(dailyWh))
    return values
  })

  const yearPrognoseData = computed(() => {
    if (level.value !== 'year' || !props.expectedProduction) return null
    return MONTHLY_SOLAR_FRACTIONS.map(f => Math.round(props.expectedProduction * f))
  })

  // ─── Sunrise/sunset markers ────────────────────────────────────────────────
  const sunriseSunsetLines = computed(() => {
    if (level.value !== 'day') return []
    const labels = aggregatedData.value.labels
    if (!labels || labels.length === 0) return []

    const date = new Date(viewYear.value, viewMonth.value, viewDay.value)
    const times = getSunriseSunset(date)
    if (!times) return []

    const lines = []
    const srIdx = findNearestLabelIndex(labels, times.sunrise)
    const ssIdx = findNearestLabelIndex(labels, times.sunset)
    if (srIdx >= 0) lines.push({ index: srIdx, color: 'rgba(251, 146, 60, 0.9)' })
    if (ssIdx >= 0) lines.push({ index: ssIdx, color: 'rgba(99, 102, 241, 0.85)' })
    return lines
  })

  // ─── Chart datasets ────────────────────────────────────────────────────────
  const chartData = computed(() => {
    const rawValues = aggregatedData.value.values
    const isDay = level.value === 'day'
    const values = normalizeValues(rawValues, mainTotalWp.value)
    const unit = showNormalized.value ? 'Wh/kWp' : 'Wh'

    // Day: a line like the neighbor overlays, but thicker, so it reads as the main series.
    // Month and year stay bars (one bar per day / month, clickable to drill down).
    const mainDataset = isDay
      ? {
          type: 'line',
          label: `Opbrengst (${unit}/kwartier)`,
          data: values,
          borderColor: 'rgba(22, 163, 74, 1)',
          backgroundColor: 'rgba(34, 197, 94, 0.75)',
          // red where the quarter's delta is negative, as the bars were
          segment: {
            borderColor: ctx => (ctx.p0.parsed.y < 0 || ctx.p1.parsed.y < 0 ? 'rgba(220, 38, 38, 1)' : undefined),
          },
          borderWidth: 3,
          pointRadius: 0,
          pointHoverRadius: 4,
          fill: false,
          tension: 0.3,
          order: 0,
        }
      : {
          type: 'bar',
          label: `Opbrengst (${unit})`,
          data: values,
          backgroundColor: values.map(v =>
            v > 0 ? 'rgba(34, 197, 94, 0.75)' : v < 0 ? 'rgba(239, 68, 68, 0.7)' : 'rgba(209, 213, 219, 0.4)'
          ),
          borderColor: values.map(v =>
            v > 0 ? 'rgba(22, 163, 74, 1)' : v < 0 ? 'rgba(220, 38, 38, 1)' : 'rgba(156, 163, 175, 0.4)'
          ),
          borderWidth: 1,
          borderRadius: 3,
          maxBarThickness: 40,
          minBarLength: 2,
          order: 2,
        }
    const datasets = isSplit.value ? [] : [mainDataset]

    // Previous period: grey, behind the main series; a dashed line by day, bars next to the bars otherwise
    if (previousData.value && !isSplit.value) {
      const previousValues = normalizeValues(previousData.value, mainTotalWp.value)
      datasets.push(isDay
        ? {
            type: 'line',
            label: previousDatasetLabel.value,
            data: previousValues,
            borderColor: 'rgba(100, 116, 139, 0.8)',
            backgroundColor: 'transparent',
            borderDash: [6, 4],
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 3,
            fill: false,
            tension: 0.3,
            spanGaps: false,
            order: 2,
          }
        : {
            type: 'bar',
            label: previousDatasetLabel.value,
            data: previousValues,
            backgroundColor: 'rgba(148, 163, 184, 0.45)',
            borderColor: 'rgba(100, 116, 139, 0.7)',
            borderWidth: 1,
            borderRadius: 3,
            maxBarThickness: 40,
            order: 3,
          })
    }

    // Forecast line
    const forecast = isDay ? prognoseData.value
      : level.value === 'month' ? monthPrognoseData.value
      : yearPrognoseData.value

    if (forecast && !isSplit.value) {
      datasets.push({
        type: 'line',
        label: 'Prognose',
        data: normalizeValues(forecast, mainTotalWp.value),
        borderColor: 'rgba(59, 130, 246, 0.9)',
        backgroundColor: 'rgba(59, 130, 246, 0.08)',
        borderWidth: 3,
        pointRadius: 0,
        fill: false,
        tension: level.value === 'year' ? 0 : 0.4,
        order: 1,
      })
    }

    // Active power line
    if (isDay && showActivePower.value && aggregatedData.value.activePower) {
      datasets.push({
        type: 'line',
        label: 'Actief vermogen (Wh)',
        data: aggregatedData.value.activePower,
        borderColor: 'rgba(225, 29, 72, 0.85)',
        backgroundColor: 'rgba(225, 29, 72, 0.05)',
        borderWidth: 2,
        pointRadius: 0,
        fill: false,
        tension: 0.3,
        order: 0,
      })
    }

    // The address's meters separately: a line each by day, bars side by side by month and year
    if (isSplit.value) {
      splitMeters.value.forEach((meter, idx) => {
        const rows = splitDataMap.value?.[meter.serial]
        if (!rows) return
        const color = METER_COLORS[idx % METER_COLORS.length]
        datasets.push(isDay
          ? {
              type: 'line',
              label: meter.label,
              data: alignToLabels(rows),
              borderColor: color,
              backgroundColor: 'transparent',
              borderWidth: 2,
              pointRadius: 0,
              pointHoverRadius: 4,
              fill: false,
              tension: 0.3,
              order: 0,
            }
          : {
              type: 'bar',
              label: meter.label,
              data: alignToLabels(rows),
              backgroundColor: color,
              borderColor: color,
              borderWidth: 1,
              borderRadius: 2,
              maxBarThickness: 24,
              order: 2,
            })
      })
    }

    // Neighbor overlays
    if (neighborDataMap.value) {
      activeNeighbors.value.forEach((meter, idx) => {
        const neighborData = neighborDataMap.value[meter.serialNumber]
        if (!neighborData || neighborData.length === 0) return

        const data = alignToLabels(neighborData)
        const neighborWp = neighborWpMap.value[meter.serialNumber]
        const normalizedData = normalizeValues(data, neighborWp || mainTotalWp.value)

        datasets.push({
          type: 'line',
          label: meter.address || meter.serialNumber,
          data: normalizedData,
          borderColor: NEIGHBOR_COLORS[idx % NEIGHBOR_COLORS.length],
          backgroundColor: 'transparent',
          borderWidth: 1.5,
          pointRadius: isDay ? 0 : 3,
          fill: false,
          tension: 0.3,
          spanGaps: false,
          order: 1,
        })
      })
    }

    return { labels: aggregatedData.value.labels, datasets }
  })

  // ─── Chart click handler ───────────────────────────────────────────────────
  const handleChartClick = (_event, elements) => {
    if (!elements || elements.length === 0) return
    const index = elements[0].index
    if (level.value === 'year') {
      viewMonth.value = aggregatedData.value.months?.[index] ?? index
      level.value = 'month'
    } else if (level.value === 'month') {
      viewDay.value = aggregatedData.value.days?.[index] ?? index + 1
      level.value = 'day'
    }
  }

  const hasExtras = computed(() =>
    isSplit.value || !!(prognoseData.value || monthPrognoseData.value || yearPrognoseData.value || activeNeighbors.value.length > 0 || showActivePower.value || previousData.value)
  )

  // ─── Congestion intervals (day view only) ─────────────────────────────────
  const activeCongestionIntervals = computed(() =>
    level.value === 'day' ? (props.congestionIntervals ?? []) : []
  )

  const labelInCongestion = (label) => {
    if (!label) return null
    const toMins = t => {
      const [h, m] = t.split(':').map(Number)
      return h * 60 + m
    }
    const target = toMins(label)
    for (const interval of activeCongestionIntervals.value) {
      const from = toMins(interval.fromLabel)
      const to = toMins(interval.toLabel)
      const [lo, hi] = from <= to ? [from, to] : [to, from]
      if (target >= lo && target <= hi) return interval
    }
    return null
  }

  // ─── Chart options ─────────────────────────────────────────────────────────
  const chartOptions = computed(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 150 },
    onClick: handleChartClick,
    // Day view is all lines without points: hovering anywhere on a quarter shows every series at that time
    interaction: level.value === 'day' ? { mode: 'index', intersect: false } : { mode: 'nearest', intersect: true },
    plugins: {
      legend: {
        display: hasExtras.value,
        labels: { font: { size: 11 }, boxWidth: 20 },
      },
      tooltip: {
        callbacks: {
          label: ctx => {
            const val = ctx.raw
            // the day view's index tooltip lists every line: skip the ones without a value at this time
            if (val == null) return null
            if (ctx.dataset.label === 'Actief vermogen (Wh)') return `${ctx.dataset.label}: ${val?.toFixed(0)} Wh`
            if (level.value === 'day') return `${ctx.dataset.label}: ${val} Wh`
            return `${ctx.dataset.label}: ${(val / 1000).toFixed(2)} kWh`
          },
          afterBody: (items) => {
            if (level.value !== 'day' || !items?.length) return null
            const label = items[0].label
            const interval = labelInCongestion(label)
            if (!interval) return null
            const peak = interval.peak != null ? ` — piek ${interval.peak.toFixed(1)} V` : ''
            return interval.severity === 'critical'
              ? [`Netcongestie kritiek (> 253 V)${peak}`]
              : [`Netcongestie (> 250 V)${peak}`]
          },
        },
      },
      verticalLines: {
        lines: sunriseSunsetLines.value,
      },
      congestionOverlay: {
        intervals: activeCongestionIntervals.value,
      },
    },
    scales: {
      x: {
        // bars need half a category of room at the edges, the day's lines do not
        offset: level.value !== 'day',
        grid: { display: false },
        ticks: {
          font: { size: 10 },
          maxRotation: level.value === 'day' ? 45 : 0,
          autoSkip: true,
          maxTicksLimit: level.value === 'day' ? 24 : 31,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          font: { size: 10 },
          callback: val => {
            const u = showNormalized.value ? 'Wh/kWp' : 'Wh'
            return level.value === 'day' ? `${val}${u}` : `${(val / 1000).toFixed(1)}k${u}`
          },
        },
      },
    },
  }))

  return {
    aggregatedData,
    chartData,
    chartOptions,
  }
}
